import React, { useState } from 'react';
import { Organigramme } from '../../../../types/organisation';
import { User, Building2, Crown, ChevronDown, ChevronRight } from 'lucide-react';

interface OrganigrammeAllSitesProps {
  organigrammes: Organigramme[];
}

interface EmployeeNode {
  id: string;
  name: string;
  position: string;
  email?: string;
  isManager: boolean;
  level: number;
  children: EmployeeNode[];
  expanded?: boolean;
  siteId: number;
  siteName: string;
  siteColor: string;
}

const OrganigrammeAllSites: React.FC<OrganigrammeAllSitesProps> = ({ organigrammes }) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeNode | null>(null);

  // Couleurs par site
  const siteColors = [
    'from-blue-500 to-blue-600',
    'from-green-500 to-green-600', 
    'from-purple-500 to-purple-600',
    'from-orange-500 to-orange-600',
    'from-pink-500 to-pink-600',
    'from-indigo-500 to-indigo-600',
    'from-teal-500 to-teal-600',
    'from-red-500 to-red-600'
  ];

  // Convertir tous les organigrammes en structure pyramidale fusionnée
  const buildMergedPyramidStructure = (): EmployeeNode[] => {
    const nodeMap = new Map<string, EmployeeNode>();
    const rootNodes: EmployeeNode[] = [];

    // Créer tous les nœuds de tous les sites en fusionnant les employés
    organigrammes.forEach((organigramme, siteIndex) => {
      const siteColor = siteColors[siteIndex % siteColors.length];
      
      organigramme.nodes.forEach(node => {
        if (node.data?.employee) {
          const employee = node.data.employee;
          const isManager = node.data.isManager || false;
          
          // Utiliser l'ID de l'employé comme clé pour fusionner les doublons
          const employeeKey = `employee_${employee.id}`;
          
          if (!nodeMap.has(employeeKey)) {
            // Créer un nouveau nœud pour cet employé
            nodeMap.set(employeeKey, {
              id: employeeKey,
              name: `${employee.firstName} ${employee.lastName}`,
              position: node.data?.position?.positionName || node.data?.employee?.position?.positionName || 'Collaborateur',
              email: employee.email,
              isManager,
              level: 0,
              children: [],
              expanded: false,
              siteId: organigramme.site.id,
              siteName: organigramme.site.siteName,
              siteColor
            });
          } else {
            // L'employé existe déjà, mettre à jour les informations si nécessaire
            const existingNode = nodeMap.get(employeeKey)!;
            if (isManager && !existingNode.isManager) {
              existingNode.isManager = true;
            }
            // Garder la couleur du premier site ou utiliser une couleur spéciale pour multi-sites
            if (existingNode.siteId !== organigramme.site.id) {
              existingNode.siteColor = 'from-purple-500 to-purple-600'; // Couleur spéciale pour multi-sites
              existingNode.siteName = 'Multi-sites';
            }
          }
        }
      });
    });

    // Construire les relations parent-enfant
    organigrammes.forEach(organigramme => {
      organigramme.edges.forEach(edge => {
        // Extraire l'ID de l'employé depuis l'edge (format: "employee_X")
        const parentEmployeeId = edge.from.replace('employee_', '');
        const childEmployeeId = edge.to.replace('employee_', '');
        
        const parentKey = `employee_${parentEmployeeId}`;
        const childKey = `employee_${childEmployeeId}`;
        
        const parent = nodeMap.get(parentKey);
        const child = nodeMap.get(childKey);
        
        if (parent && child && !parent.children.some(c => c.id === child.id)) {
          parent.children.push(child);
        }
      });
    });

    // Trouver les nœuds racines (ceux qui n'ont pas de parent)
    const hasParent = new Set<string>();
    organigrammes.forEach(organigramme => {
      organigramme.edges.forEach(edge => {
        const childEmployeeId = edge.to.replace('employee_', '');
        hasParent.add(`employee_${childEmployeeId}`);
      });
    });

    nodeMap.forEach((node, id) => {
      if (!hasParent.has(id)) {
        rootNodes.push(node);
      }
    });

    // Calculer les niveaux
    const calculateLevels = (nodes: EmployeeNode[], level: number = 0) => {
      nodes.forEach(node => {
        node.level = level;
        if (node.children.length > 0) {
          calculateLevels(node.children, level + 1);
        }
      });
    };

    calculateLevels(rootNodes);
    return rootNodes;
  };

  const pyramidData = buildMergedPyramidStructure();

  const toggleNodeExpansion = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const getNodeIcon = (node: EmployeeNode) => {
    if (node.isManager) {
      return <Crown className="h-2.5 w-2.5 text-yellow-600 flex-shrink-0" />;
    }
    return <User className="h-2.5 w-2.5 text-white flex-shrink-0" />;
  };

  const getNodeColor = (node: EmployeeNode) => {
    return `bg-gradient-to-br ${node.siteColor} border-white`;
  };

  const renderNode = (node: EmployeeNode, level: number = 0): React.ReactNode => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children.length > 0;

    return (
      <div key={`${node.id}-${level}`} className="flex flex-col items-center">
        {/* Nœud */}
        <div 
          className={`
            relative p-3 rounded-xl border-2 shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer
            ${getNodeColor(node)}
            min-w-[140px] max-w-[180px]
          `}
          onDoubleClick={() => {
            setSelectedEmployee(node);
          }}
        >
          {/* Photo de profil avec couleur du site */}
          <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-white/20 border-2 border-white/30 shadow-sm flex items-center justify-center">
            {getNodeIcon(node)}
          </div>
          
          {/* Nom */}
          <h3 className="text-sm font-bold text-white text-center mb-1">
            {node.name}
          </h3>
          
          {/* Poste */}
          <p className="text-xs text-white/90 text-center mb-1">
            {node.data?.position?.positionName || node.data?.employee?.position?.positionName || 'Collaborateur'}
          </p>

          {/* Site */}
          <p className="text-xs text-white/80 text-center mb-1">
            {node.siteName}
          </p>
          
          {/* Email au survol */}
          {node.email && (
            <div className="group relative">
              <div className="w-2 h-2 bg-white/60 rounded-full mx-auto cursor-help"></div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                {node.email}
              </div>
            </div>
          )}
          
          {/* Badge Manager */}
          {node.isManager && (
            <div className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs px-1.5 py-0.5 rounded-full">
              M
            </div>
          )}

          {/* Bouton d'expansion */}
          {hasChildren && (
            <button
              onClick={() => toggleNodeExpansion(node.id)}
              className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-white border-2 border-gray-300 rounded-full p-1 hover:bg-gray-50 transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="h-3 w-3 text-gray-600" />
              ) : (
                <ChevronRight className="h-3 w-3 text-gray-600" />
              )}
            </button>
          )}
        </div>

        {/* Lignes de connexion et enfants */}
        {hasChildren && isExpanded && (
          <div className="mt-4 flex flex-col items-center">
            {/* Ligne verticale */}
            <div className="w-px h-4 bg-gray-300 mb-2"></div>
            
            {/* Ligne horizontale */}
            <div className="w-full h-px bg-gray-300 mb-4"></div>
            
            {/* Enfants */}
            <div className="flex flex-wrap justify-center gap-4">
              {node.children.map((child, index) => (
                <div key={`${child.id}-${level + 1}-${index}`} className="flex flex-col items-center">
                  {/* Ligne verticale vers l'enfant */}
                  <div className="w-px h-4 bg-gray-300 mb-2"></div>
                  {renderNode(child, level + 1)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (pyramidData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <p>Aucune hiérarchie trouvée</p>
        </div>
      </div>
    );
  }

  // Calculer les stats totales
  const totalEmployees = organigrammes.reduce((sum, org) => sum + org.stats.totalEmployees, 0);
  const totalManagers = organigrammes.reduce((sum, org) => sum + org.stats.totalManagers, 0);

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-max py-6">
        {/* Titre */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Organigramme - Tous les Sites
          </h2>
          <p className="text-sm text-gray-600">
            {totalEmployees} employés • {totalManagers} managers • {organigrammes.length} sites
          </p>
        </div>

        {/* Instructions */}
        <div className="text-center mb-6">
          <p className="text-sm text-gray-500">
            Cliquez sur les flèches pour développer/réduire les niveaux
          </p>
        </div>

        {/* Pyramide */}
        <div className="flex justify-center">
          <div className="flex flex-col items-center space-y-6">
            {pyramidData.map((rootNode, index) => (
              <div key={`root-${rootNode.id}-${index}`} className="flex flex-col items-center">
                {renderNode(rootNode, 0)}
              </div>
            ))}
          </div>
        </div>

        {/* Légende avec couleurs des sites */}
        <div className="mt-8 flex justify-center">
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Légende</h4>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="flex items-center space-x-2">
                <ChevronDown className="h-3 w-3 text-gray-500" />
                <span className="text-gray-600">Développer</span>
              </div>
              <div className="flex items-center space-x-2">
                <ChevronRight className="h-3 w-3 text-gray-500" />
                <span className="text-gray-600">Réduire</span>
              </div>
            </div>
            
            {/* Couleurs des sites */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <h5 className="text-xs font-medium text-gray-700 mb-2">Couleurs par site :</h5>
              <div className="flex flex-wrap gap-2">
                {organigrammes.map((org, index) => (
                  <div key={org.site.id} className="flex items-center space-x-1">
                    <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${siteColors[index % siteColors.length]}`}></div>
                    <span className="text-xs text-gray-600">{org.site.siteName}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popup employé */}
      {selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Informations employé</h3>
              <button
                onClick={() => setSelectedEmployee(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Nom complet</label>
                <p className="text-gray-900">{selectedEmployee.name}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Poste</label>
                <p className="text-gray-900">{selectedEmployee.position}</p>
              </div>
              
              {selectedEmployee.email && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <p className="text-gray-900">{selectedEmployee.email}</p>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium text-gray-700">Site(s)</label>
                <p className="text-gray-900">{selectedEmployee.siteName}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Statut</label>
                <p className="text-gray-900">
                  {selectedEmployee.isManager ? 'Manager' : 'Collaborateur'}
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedEmployee(null)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganigrammeAllSites;
